import React, { ChangeEvent, useEffect, useRef, useState } from "react";

import {
  Typography,
  Select,
  SelectData,
  Button,
  SelectEvent,
} from "@king-ict/eupisi-ui/components";
import { useInput } from "@king-ict/eupisi-ui/hooks";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { getPedagogicalYears, getMonths } from "../../api";
import { useTypedSelector } from "../../app";
import { PedagogicalYear } from "../../models";
import styles from "./AdministrationListFilters.module.scss";
import { AdministrationListFiltersData } from "./AdministrationListFiltersData";

export interface AdministrationListFiltersChildProps<FilterType> {
  filters?: FilterType;
  onFilterChange?: (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >
  ) => void;
}

export interface AdministrationListFiltersProps {
  children?: React.ReactNode;
  isFilteredByYear?: boolean;
  isFilteredByMonth?: boolean;
  callbackOnFiltersChanged?: boolean;
  callbackOnSearchClick?: boolean;
  onFiltersChanged?: (filters: any) => void;
  initialFilterValue?: any;
}

export const AdministrationListFilters = ({
  children,
  isFilteredByYear,
  isFilteredByMonth,
  callbackOnFiltersChanged,
  callbackOnSearchClick,
  onFiltersChanged,
  initialFilterValue,
}: AdministrationListFiltersProps) => {
  const { t } = useTranslation();
  const { handleChange } = useInput();

  const currentImportantDate = useTypedSelector(
    (state) => state.application.importantDate
  );

  const currentPedagogicalYear = useTypedSelector(
    (state) => state.application.pedagogicalYear
  );

  const [filters, setFilters] = useState<AdministrationListFiltersData>();
  const [pedagogicalYears, setPedagogicalYears] = useState<PedagogicalYear[]>(
    []
  );
  const [months, setMonths] = useState<CodeBook[]>([]);
  const isFirstRender = useRef(true);

  useEffect(() => {
    let years: PedagogicalYear[] = [];
    let months: CodeBook[] = [];
    isFirstRender.current = true;
    if (isFilteredByYear) {
      (async () => {
        years = await getPedagogicalYears();
        if (years?.length > 0) {
          setPedagogicalYears(years);
        }
      })();
    }
    if (isFilteredByMonth) {
      (async () => {
        months = await getMonths();
        if (months?.length > 1) {
          setMonths(months);
        }
      })();
    }

    setFilters((prev) => {
      return {
        ...prev,
        pedagogicalYearId: isFilteredByYear
          ? currentPedagogicalYear.id
          : undefined,
        monthId: isFilteredByMonth ? currentImportantDate.monthId : undefined,
      };
    });
  }, [
    isFilteredByYear,
    isFilteredByMonth,
    currentImportantDate.monthId,
    currentPedagogicalYear,
  ]);

  useEffect(() => {
    if (!onFiltersChanged || !filters) {
      return;
    }

    if (isFirstRender.current || callbackOnFiltersChanged) {
      onFiltersChanged(filters);
      isFirstRender.current = false;
    }
  }, [onFiltersChanged, callbackOnFiltersChanged, filters]);

  // TODO: Istead of clonning, implement render props pattern
  // (as implemented in AdministrationCreateUpdate component)
  const cloneChildren = () => {
    if (children) {
      return React.Children.toArray(children).map((c: any) =>
        React.cloneElement(c, {
          filters: filters,
          onFilterChange: (e: any) => handleChange(e, setFilters),
        })
      );
    }

    return null;
  };

  useEffect(() => {
    if (initialFilterValue) {
      setFilters(initialFilterValue);
      if (!initialFilterValue.pedagogicalYearId && isFilteredByYear) {
        setFilters(() => {
          return {
            ...initialFilterValue,  
            pedagogicalYearId: isFilteredByYear
              ? filters?.pedagogicalYearId || currentPedagogicalYear.id
              : undefined,            
          };
        });
      }

      if (!initialFilterValue.monthId && isFilteredByMonth) {
        setFilters(() => {
          return {
            ...initialFilterValue,           
            monthId: isFilteredByMonth 
              ? filters?.monthId || currentImportantDate.monthId
              : undefined,
          };
        });
      }
      
    }
  }, [initialFilterValue, isFilteredByYear, isFilteredByMonth]);

  const handleSearch = () => {
    if (onFiltersChanged && callbackOnSearchClick) {
      onFiltersChanged(filters);
    }
  };

  const handleClear = () => {
    setFilters((prev) => {
      return {
        pedagogicalYearId: prev?.pedagogicalYearId,
        monthId: isFilteredByMonth ? currentImportantDate.monthId : undefined,
      } as AdministrationListFiltersData;
    });
  };

  return (
    <>
      <Row className={styles.margin1em}>
        {isFilteredByYear && (
            <Col xs={6}>
              <div className="form-inline">
            <Typography variant="baseBold" component="span">
                {t("common.pedagogicalYear")}
            </Typography>
            
                <Select
                  borderless
                  name={nameof<AdministrationListFiltersData>(
                    "pedagogicalYearId"
                  )}
                  value={filters?.pedagogicalYearId}
                  onChange={(e) => handleChange(e, setFilters)}
                  selectData={
                    {
                      valueProp: "id",
                      textProp: "name",
                      data: pedagogicalYears,
                    } as SelectData<PedagogicalYear>
                  }
                />
                </div>
            </Col>              
          )}

          {isFilteredByMonth && (
            <Col xs={6}>
            <div className="form-inline">
              <Typography variant="baseBold" component="span">
                  {t("common.month")}
                </Typography>
                <Select
                    borderless
                    emptyOption={{
                      value: undefined,
                      text: t("common.allMonths"),
                    }}
                    className={styles.minSelectWidth}
                    name={nameof<AdministrationListFiltersData>("monthId")}
                    value={filters?.monthId}
                    onChange={(e) => handleChange(e, setFilters)}
                    selectData={
                      {
                        valueProp: "id",
                        textProp: "name",
                        data: months,
                      } as SelectData<CodeBook>
                    }
                  />
                </div>
                  
            </Col>
          )}          
      </Row>
      
      {cloneChildren()}
      {callbackOnSearchClick && (
        <Row>
          <Col md={12}>
            <div className={styles.searchButtons}>
              <Button color="primary" onClick={handleSearch}>
                {t("common.search")}
              </Button>
              <Typography color="primary" onClick={handleClear}>
                {t("common.clearFilters")}
              </Typography>
            </div>
          </Col>
        </Row>
      )}
    </>
  );
};
